/**
 * Student Quiz List Page - Modernized
 * Displays list of quiz assignments for students with modern UI matching Assignments page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiSearch, FiClock, FiCheckCircle, FiAlertCircle, FiPlay, FiEye, FiCalendar, FiFileText } from 'react-icons/fi';
import { formatUTCToChicago } from '../../utils/datetime-comprehensive';
import quizApiService from '../../services/quizApi';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 1.875rem;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SearchFilterRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: nowrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const SearchBar = styled.div`
  position: relative;
  flex: 3;
  min-width: 0;
  
  @media (max-width: 768px) {
    flex: none;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #E5E3DF;
  border-radius: 10px;
  font-size: 0.875rem;
  background-color: white;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  min-height: 44px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  width: 1rem;
  height: 1rem;
  pointer-events: none;
`;

const CourseFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 200px;
  flex: 2;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    flex: none;
    gap: 0.75rem;
    align-items: center;
  }
`;

const CourseFilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }
`;

const CourseFilterSelect = styled.select`
  flex: 1;
  min-width: 0;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  border: 1px solid #E5E3DF;
  border-radius: 10px;
  font-size: 0.875rem;
  background-color: white;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-height: 44px;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 16px;
    flex: 1;
    min-width: 0;
    width: 100%;
    padding: 0.875rem 2.75rem 0.875rem 1rem;
    background-position: right 0.875rem center;
    background-size: 14px;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const FilterPill = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : '#E5E3DF'};
  background: ${props => props.active ? 'var(--primary-color)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  border-radius: 24px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.active ? 'var(--primary-dark)' : '#F5F4F1'};
    border-color: ${props => props.active ? 'var(--primary-dark)' : '#D0CEC9'};
    transform: translateY(-1px);
  }
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.375rem;
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 600;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(216, 184, 137, 0.15)'};
  color: ${props => props.active ? 'white' : 'var(--primary-color)'};
`;

const QuizList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const QuizCard = styled(motion.div)`
  background: white;
  border: 1px solid #E5E3DF;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #D0CEC9;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const QuizTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  letter-spacing: -0.02em;
  font-family: 'Inter', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const CourseInfo = styled.p`
  color: var(--text-muted);
  font-size: 0.875rem;
  font-weight: 400;
  margin: 0 0 0.75rem 0;
  font-family: 'Inter', sans-serif;

  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.5rem;
  opacity: 0.85;
  font-family: 'Inter', sans-serif;
  line-height: 1.33;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    color: var(--text-muted);
    opacity: 0.6;
  }
`;

const MetaSeparator = styled.span`
  color: var(--text-muted);
  opacity: 0.5;
`;

const StatusBadge = styled.div`
  padding: 0.5rem 0.875rem;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  align-self: flex-start;
  
  ${props => {
    switch (props.status) {
      case 'completed':
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
        `;
      case 'overdue':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
        `;
      case 'in_progress':
        return `
          background: rgba(251, 191, 36, 0.1);
          color: #d97706;
        `;
      default:
        return `
          background: rgba(148, 163, 184, 0.1);
          color: #64748b;
        `;
    }
  }}
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const CardContent = styled.div`
  padding: 0 1.5rem 1.5rem;

  @media (max-width: 768px) {
    padding: 0 1.25rem 1.25rem;
  }
`;

const DetailsBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0;
  background: transparent;
  border-radius: 0;
`;

const DetailPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--text-secondary);
  font-size: 0.8125rem;
  font-weight: 500;
  font-family: 'Inter', sans-serif;

  svg {
    color: var(--primary-color);
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  
  &.primary {
    background: var(--primary-color);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(216, 184, 137, 0.3);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background: #10b981;
    color: white;

    &:hover {
      background: #059669;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.8125rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    color: var(--text-primary);
    margin-bottom: 0.5rem;
    font-size: 1.25rem;
    font-weight: 600;
  }
  
  p {
    color: var(--text-muted);
    max-width: 400px;
    margin: 0 auto;
  }
`;

const LoadingState = styled(EmptyState)``;
const ErrorState = styled(EmptyState)`
  color: var(--error-color);
`;

function StudentQuizList() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('open');
  const [courseFilter, setCourseFilter] = useState('active');
  const [courses, setCourses] = useState([]);
  const [startingQuizId, setStartingQuizId] = useState(null);

  // Helper functions for status determination
  const isCompleted = (assignment) => {
    return assignment.status === 'completed' || assignment.submitted_at !== null;
  };

  const isOverdue = (assignment) => {
    if (isCompleted(assignment)) return false;
    const dueDate = assignment.assignment_due_at || assignment.quiz_due_at;
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getUiStatus = (assignment) => {
    if (isCompleted(assignment)) return 'completed';
    if (isOverdue(assignment)) return 'overdue';
    return 'open';
  };

  const fetchCourses = useCallback(async () => {
    try {
      const response = await quizApiService.getCourses();
      if (response.success) {
        setCourses(response.courses || response.data || []);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  }, []);

  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await quizApiService.getStudentAssignments({ limit: 1000 });
      
      if (response.success && response.data) {
        setAssignments(response.data);
      } else {
        setError('Failed to load quiz assignments');
      }
    } catch (err) {
      console.error('Error fetching quiz assignments:', err);
      setError('Failed to load quiz assignments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssignments();
    fetchCourses();
  }, [fetchAssignments, fetchCourses]);

  const getStatusText = (uiStatus) => {
    switch (uiStatus) {
      case 'completed': return 'Completed';
      case 'overdue': return 'Overdue';
      case 'open': return 'Open';
      default: return 'Open';
    }
  };

  const getStatusIcon = (uiStatus) => {
    switch (uiStatus) {
      case 'completed': return <FiCheckCircle />;
      case 'overdue': return <FiAlertCircle />;
      case 'open': return <FiClock />;
      default: return <FiClock />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No Due Date';
    // Format: "January 15, 2026 at 6:59 PM" (matching Assignments style)
    return formatUTCToChicago(dateString, `MMMM d, yyyy 'at' h:mm a`);
  };

  const handleStartQuiz = (assignmentId) => {
    if (startingQuizId) return;
    setStartingQuizId(assignmentId);
    navigate(`/student/assignments/${assignmentId}/warning`);
  };

  // Determine active courses
  const activeCourses = courses.filter(course => {
    // Course is active if is_active is true AND (no end_date OR end_date is in the future)
    const isActive = course.is_active === true || course.is_active === 1;
    const hasNoEndDate = !course.end_date || course.end_date === null;
    const endDateInFuture = course.end_date ? new Date(course.end_date) >= new Date() : false;
    return isActive && (hasNoEndDate || endDateInFuture);
  });
  const activeCourseIds = new Set(activeCourses.map(c => c.id));
  const inactiveCourses = courses.filter(c => !activeCourseIds.has(c.id));

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = assignment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (assignment.course_name && assignment.course_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const uiStatus = getUiStatus(assignment);
    const matchesFilter = activeFilter === uiStatus;
    
    // Course filtering logic
    let matchesCourse = false;
    if (courseFilter === 'active') {
      // Show only quizzes from active courses
      matchesCourse = assignment.course_id && activeCourseIds.has(parseInt(assignment.course_id));
    } else if (courseFilter === 'all') {
      // Show all quizzes (both active and inactive courses)
      matchesCourse = true;
    } else {
      // Show quizzes from a specific course (active or inactive)
      matchesCourse = courseFilter && assignment.course_id && parseInt(assignment.course_id) === parseInt(courseFilter);
    }
    
    return matchesSearch && matchesFilter && matchesCourse;
  });

  // Filter counts should respect the course filter selection
  const assignmentsForCounts = assignments.filter(assignment => {
    if (courseFilter === 'active') {
      return assignment.course_id && activeCourseIds.has(parseInt(assignment.course_id));
    } else if (courseFilter === 'all') {
      return true;
    } else {
      return courseFilter && assignment.course_id && parseInt(assignment.course_id) === parseInt(courseFilter);
    }
  });

  const filterCounts = {
    open: assignmentsForCounts.filter(a => getUiStatus(a) === 'open').length,
    completed: assignmentsForCounts.filter(a => getUiStatus(a) === 'completed').length,
    overdue: assignmentsForCounts.filter(a => getUiStatus(a) === 'overdue').length,
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>My Quizzes</Title>
        </Header>
        <LoadingState>
          <FiClock size={48} />
          <h3>Loading quizzes...</h3>
          <p>Please wait while we fetch your quiz assignments.</p>
        </LoadingState>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Title>My Quizzes</Title>
        </Header>
        <ErrorState>
          <FiAlertCircle size={48} />
          <h3>Error loading quizzes</h3>
          <p>{error}</p>
        </ErrorState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>My Quizzes</Title>
      </Header>

      {/* Search and Filter Row */}
      <SearchFilterRow>
        <SearchBar>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search quizzes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBar>

        <CourseFilterContainer>
          <CourseFilterLabel>Course:</CourseFilterLabel>
          <CourseFilterSelect
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
          >
            <option value="active">All Active Courses</option>
            {activeCourses.length === 0 && inactiveCourses.length === 0 && (
              <option value="" disabled>No courses available</option>
            )}
            {activeCourses.length > 0 && activeCourses.map(course => (
              <option key={course.id} value={course.id}>
                {course.code} - {course.name}
              </option>
            ))}
            {inactiveCourses.length > 0 && (
              <>
                <option disabled>──────────</option>
                <option disabled>Past Courses</option>
                {inactiveCourses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.code} - {course.name}
                  </option>
                ))}
              </>
            )}
          </CourseFilterSelect>
        </CourseFilterContainer>
      </SearchFilterRow>

      <Filters>
        <FilterPill 
          active={activeFilter === 'open'} 
          onClick={() => setActiveFilter('open')}
        >
          Open
          <Count active={activeFilter === 'open'}>{filterCounts.open}</Count>
        </FilterPill>
        <FilterPill 
          active={activeFilter === 'completed'} 
          onClick={() => setActiveFilter('completed')}
        >
          Completed
          <Count active={activeFilter === 'completed'}>{filterCounts.completed}</Count>
        </FilterPill>
        <FilterPill 
          active={activeFilter === 'overdue'} 
          onClick={() => setActiveFilter('overdue')}
        >
          Overdue
          <Count active={activeFilter === 'overdue'}>{filterCounts.overdue}</Count>
        </FilterPill>
      </Filters>

      {filteredAssignments.length > 0 ? (
        <QuizList>
          {filteredAssignments.map((assignment, index) => (
            <QuizCard
              key={assignment.assignment_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <CardHeader>
                <CardInfo>
                  <QuizTitle>{assignment.title}</QuizTitle>
                  <CourseInfo>
                    {assignment.course_name || 'No course assigned'}
                    {(assignment.instructor_first_name || assignment.instructor_last_name) && (
                      <span> • {[assignment.instructor_first_name, assignment.instructor_last_name].filter(Boolean).join(' ')}</span>
                    )}
                  </CourseInfo>
                  <MetaRow>
                    <MetaItem>
                      <FiCalendar size={14} />
                      Due Date: {formatDate(assignment.assignment_due_at || assignment.quiz_due_at)}
                    </MetaItem>
                    <MetaSeparator>•</MetaSeparator>
                    <MetaItem>
                      <FiClock size={14} />
                      {assignment.time_limit_minutes ? `${assignment.time_limit_minutes} min` : 'No time limit'}
                    </MetaItem>
                    <MetaSeparator>•</MetaSeparator>
                    <MetaItem>
                      <FiFileText size={14} />
                      {assignment.attempts_allowed} {assignment.attempts_allowed === 1 ? 'attempt' : 'attempts'}
                    </MetaItem>
                  </MetaRow>
                </CardInfo>
                <StatusBadge status={getUiStatus(assignment)}>
                  {getStatusIcon(getUiStatus(assignment))}
                  {getStatusText(getUiStatus(assignment))}
                </StatusBadge>
              </CardHeader>
              
              <CardContent>
                <DetailsBox>
                  {assignment.submitted_at && (
                    <>
                      <DetailPill>
                        <FiCheckCircle />
                        Submitted: {formatDate(assignment.submitted_at)}
                      </DetailPill>
                      {(() => {
                        const dueDate = new Date(assignment.assignment_due_at || assignment.quiz_due_at);
                        const submittedDate = new Date(assignment.submitted_at);
                        return submittedDate > dueDate ? (
                          <DetailPill style={{ color: '#dc2626' }}>
                            <FiAlertCircle />
                            Submitted Late
                          </DetailPill>
                        ) : null;
                      })()}
                    </>
                  )}
                  {assignment.score !== null && assignment.score !== undefined && (
                    <DetailPill>
                      <FiCheckCircle />
                      Score: {assignment.score}/{assignment.max_score}
                    </DetailPill>
                  )}
                </DetailsBox>
                
                <Actions>
                  {!isCompleted(assignment) && !assignment.latest_attempt_id && (
                    <Button 
                      className="primary"
                      onClick={() => handleStartQuiz(assignment.assignment_id)}
                      disabled={startingQuizId === assignment.assignment_id}
                    >
                      <FiPlay />
                      {startingQuizId === assignment.assignment_id ? 'Starting...' : 'Start Quiz'}
                    </Button>
                  )}
                  {!isCompleted(assignment) && assignment.latest_attempt_id && (
                    <Button 
                      className="primary"
                      onClick={() => navigate(`/student/attempt/${assignment.latest_attempt_id}`)}
                    >
                      <FiPlay />
                      {assignment.attempts_allowed === 1 ? 'Complete Quiz' : 'Continue Quiz'}
                    </Button>
                  )}
                  {isCompleted(assignment) && (
                    <Button 
                      className="secondary"
                      onClick={() => navigate(`/student/result/${assignment.latest_attempt_id}`)}
                    >
                      <FiEye />
                      View Results
                    </Button>
                  )}
                </Actions>
              </CardContent>
            </QuizCard>
          ))}
        </QuizList>
      ) : (
        <EmptyState>
          <FiFileText />
          <h3>No quizzes found</h3>
          <p>
            {searchTerm || activeFilter !== 'open' || courseFilter !== 'active'
              ? 'Try adjusting your search or filters to find what you\'re looking for.'
              : 'You don\'t have any quiz assignments yet.'
            }
          </p>
        </EmptyState>
      )}
    </Container>
  );
}

export default StudentQuizList;
